import axios, { AxiosError, AxiosResponse } from 'axios';
import axiosRetry from 'axios-retry';

import { useAuthStore } from 'src/auth/auth.store';

import { setAxiosAuthorization } from './axios-authorization-header';
import { AxiosConnectionError } from './errors';
import { HttpTypes } from './types.http';

const _api = axios.create({
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosRetry(_api, { retries: 3 });

export class AxiosHttpClient implements HttpTypes.HttpClient {
  private readonly url: string = '';

  private refreshingTokenPromise: Promise<void> | null = null;

  constructor(
    private readonly baseURL: string,
    private readonly interceptors?: any
  ) {
    const auth = useAuthStore.getState();
    if (auth.accessToken && _api.defaults.headers.Authorization === undefined) {
      setAxiosAuthorization(_api, auth.accessToken);
    }
    if (this.baseURL) {
      this.url = this.baseURL;
    }
    if (interceptors) {
      this.interceptors?.forEach((interceptor: ['request' | 'response', ...any[]]) => {
        const [type, ...args] = interceptor;
        _api.interceptors[type].use(...args);
      });
    }
  }

  private async refreshTokensOnce(): Promise<void> {
    if (!this.refreshingTokenPromise) {
      this.refreshingTokenPromise = this.getTokens().finally(() => {
        this.refreshingTokenPromise = null;
      });
    }
    return this.refreshingTokenPromise;
  }

  async getTokens(): Promise<void> {
    const auth = useAuthStore.getState();
    const { refreshToken } = auth;
    if (!refreshToken) {
      auth.clearTokens();
      return;
    }
    const data: HttpTypes.HttpRequest = {
      url: 'admin-auth/refresh',
      method: 'post',
      body: { refreshToken },
      headers: {
        'Content-Type': 'application/json',
      },
    };
    const response = await this.request(data);
    const { accessToken } = response.body as any;
    if (accessToken && refreshToken) {
      auth.setTokens(accessToken, refreshToken);
      setAxiosAuthorization(_api, accessToken);
    } else {
      auth.clearTokens();
    }
  }

  async request(data: HttpTypes.HttpRequest, retryToGetTokens: boolean = true): Promise<HttpTypes.HttpResponse> {
    const auth = useAuthStore.getState();
    let axiosResponse: AxiosResponse;
    try {
      axiosResponse = await _api.request({
        url: `${this.url}/${data.url}`,
        method: data.method,
        data: data.body,
        params: data.params,
        responseType: data.responseType,
        headers: { ...data.headers },
      });
    } catch (error: any) {
      const errors = error as AxiosError;
      if (error?.response?.status === 403 && auth.refreshToken && retryToGetTokens) {
        try {
          await this.refreshTokensOnce();
          if (auth.isAuthenticated) {
            return await this.request(data, false);
          }
        } catch (refereshError: any) {
          console.log('[ERROR]', refereshError.message);
        }
      }
      if (!retryToGetTokens) {
        auth.clearTokens();
      }
      if (errors.response) {
        axiosResponse = errors?.response;
      } else if (errors.request) {
        axiosResponse = errors?.request;
      } else {
        axiosResponse = AxiosConnectionError as AxiosResponse;
      }
    }
    return {
      statusCode: axiosResponse.status,
      body: axiosResponse.data,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async fakeRequest(data: HttpTypes.HttpRequest, options: HttpTypes.RequestOptions): Promise<HttpTypes.HttpResponse> {
    const fakeResponse: HttpTypes.HttpResponse = {
      body: options.data,
      statusCode: options.status,
    };
    if (options.delay) {
      await new Promise((resolve) => setTimeout(resolve, options.delay));
    }
    return Promise.resolve(fakeResponse);
  }
}
